import {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {captureException} from '@sentry/react';
import {LearningPathNestedStep} from 'utils/types/learningPath';
import {Activity} from 'APIs/letrusAPI/activities/types';
import {fetchActivitiesRequest} from 'APIs/letrusAPI/activities';
import {cachedActivity} from 'APIs/letrusAPI/activities/activities.caches';
// Deliberate inversion (feature importing from a container): TrailCompleted owns the
// trail_summary contract/payload translation, and creating a shared layer just for this single
// consumer would be premature abstraction (code review finding, 2026-09-22).
import {fetchTrailCompletedService} from 'containers/TrailCompleted/trailCompleted.service';

enum RouteByActivityType {
  questionnaire = '/atividades/pesquisa',
  'pedagogical content' = '/atividades/leitura',
  write = '/atividades/escrita',
  evaluative = '/atividades/simulado',
}

function useLearningPathNavigation(learningPathRef?: string | Activity) {
  const [learningPath, setLearningPath] = useState<Activity>();

  const navigate = useNavigate();
  const {data: activities} = fetchActivitiesRequest();

  useEffect(() => {
    if (typeof learningPathRef === 'string') {
      // We've already resolved this learning path before (student is already inside the flow) —
      // don't let an SWR revalidation of /api/v1/activities/ (e.g. reconnecting after a while
      // idle on the page, `dedupingInterval: 0`) kick the student back to home just because this
      // new response briefly had the status outside the expected allowlist (live finding,
      // 2026-09-23: trail redirecting to the start on its own after a while). Only the initial
      // resolution needs to validate the status; once inside, the trail's internal navigation is
      // in charge.
      if (learningPath?.id === Number(learningPathRef)) return;

      if (activities) {
        Object.values(activities).forEach((activityList: Activity[]) => {
          activityList.forEach((activity) => {
            const isSelectedLearningPath =
              activity.id === Number(learningPathRef);

            const isAvailableLearningPath =
              activity?.status === 'expired' ||
              activity?.status === 'in_progress' ||
              activity?.status === 'finished';

            // redirects to dashboard if the learningPath do not exist
            if (!activity) {
              navigate('/');
            }

            if (isSelectedLearningPath) {
              if (isAvailableLearningPath) {
                setLearningPath(activity);
              } else {
                // redirects to dashboard if receives an expired or scheduled learningPath
                navigate('/');
              }
            }
          });
        });
      }
    } else {
      setLearningPath(learningPathRef);
    }
  }, [learningPathRef, activities]);

  async function redirectToStep(step?: LearningPathNestedStep) {
    if (step && learningPath) {
      const isEvaluative = learningPath.learning_path_type === 'evaluative';
      const isFocusModeQuestionnaire =
        learningPath.uses_focus_mode && step.activity_type === 'questionnaire';

      const activityTypeKey =
        isEvaluative || isFocusModeQuestionnaire
          ? 'evaluative'
          : step.activity_type;

      const route = RouteByActivityType[activityTypeKey];

      cachedActivity
        .updateStepNumber(learningPath.id, step.order)
        .catch(() => {});

      if (!step.activity_id) {
        navigate({
          pathname: `/atividades/escrita/criar`,
          search: `trilha=${learningPath.id}`,
        });
      } else {
        navigate({
          pathname: `${route}/${step.activity_id}`,
          search: `trilha=${learningPath.id}${
            isEvaluative ? `&step=${step.id}` : ''
          }`,
        });
      }
    }
  }

  function goToCurrentStep() {
    const currentStep = learningPath?.steps?.find(
      (step) => step.order === learningPath.step_number,
    );

    redirectToStep(currentStep);
  }

  // Single questionnaire-type step and "school" path (not evaluative/write) — this is not
  // definitive proof that it's a Reading Cycle trail (the front doesn't receive
  // `follows_reading_cycle` or `level` in `/api/v1/activities/`, only the back really knows), but
  // it narrows the `trail_summary` probing to a small subset of trails instead of running on
  // every finish on the site (SAEB, writing, research never match here).
  function isPossibleReadingCycleTrail(activity?: Activity): boolean {
    return (
      activity?.learning_path_type === 'school' &&
      activity.steps.length === 1 &&
      activity.steps[0].activity_type === 'questionnaire'
    );
  }

  async function finishLearningPath() {
    cachedActivity.updateFinishDate(learningPath?.id).catch(() => {});

    if (learningPath && isPossibleReadingCycleTrail(learningPath)) {
      try {
        await fetchTrailCompletedService(learningPath.id);
        navigate(`/atividades/trilha/${learningPath.id}/concluida`);
        return;
      } catch (error) {
        // 400/404: back confirms it's not a Reading Cycle trail (expected, silently continues to
        // the standard flow below). Any other status/network error is anomalous — a real Reading
        // Cycle trail would lose its result screen without anyone noticing, so we log it.
        const status = (error as {response?: {status?: number}})?.response
          ?.status;
        if (status !== 400 && status !== 404) {
          captureException(error, {
            tags: {feature: 'trail_summary_probe'},
            extra: {status},
          });
        }
      }
    }

    const isFocusMode = Boolean(learningPath?.uses_focus_mode);
    const shouldRedirectToCSAT =
      learningPath?.get_opinion && learningPath?.status !== 'finished';
    const shouldForceReload = isFocusMode && !shouldRedirectToCSAT;

    const targetPath = shouldRedirectToCSAT
      ? `/atividades/trilha/${learningPath?.id}/pesquisa`
      : `/`;

    if (learningPath) {
      if (shouldForceReload) {
        window.location.replace(targetPath);
        return;
      }

      if (shouldRedirectToCSAT) {
        navigate(
          targetPath,
          isFocusMode ? {state: {focusModeComplete: true}} : undefined,
        );
        return;
      }

      navigate(
        targetPath,
        isFocusMode ? {state: {focusModeComplete: true}} : undefined,
      );
    }
  }

  function goToNextStep(currentActivityId?: number) {
    if (!learningPath) return;

    const currentStepOrder = currentActivityId
      ? learningPath.steps?.find(
          (step) => step.activity_id === currentActivityId,
        )?.order ??
        learningPath.step_number ??
        0
      : learningPath.step_number ?? 0;

    const nextStepOrder = currentStepOrder + 1;

    const nextStep = learningPath.steps?.find(
      (step) => step.order === nextStepOrder,
    );

    const isLastStep = !nextStep; // if nextStep is undefined, we assume the learningPath is over
    if (isLastStep) {
      finishLearningPath();
    } else {
      redirectToStep(nextStep);
    }
  }

  function goToPreviousStep(currentActivityId?: number) {
    if (!learningPath) return;

    const currentStepOrder = currentActivityId
      ? learningPath.steps?.find(
          (step) => step.activity_id === currentActivityId,
        )?.order ??
        learningPath.step_number ??
        0
      : learningPath.step_number ?? 0;

    const previousStepOrder = currentStepOrder - 1;

    const previousStep = learningPath.steps?.find(
      (step) => step.order === previousStepOrder,
    );

    if (previousStep) {
      redirectToStep(previousStep);
    } else {
      navigate('/');
    }
  }

  function getOrderByLastReview() {
    const writeSteps = learningPath?.steps?.filter(
      (step) => step.activity_type === 'write' && !!step.activity_status,
    );

    if (writeSteps?.length) {
      if (writeSteps.length > 1) {
        return writeSteps[writeSteps.length - 1]?.order || 1;
      }
      return writeSteps[0]?.order || 1;
    }
    return 1;
  }

  function goToLastReviewOrFirstStep() {
    const order = getOrderByLastReview();

    const stepByOrder = learningPath?.steps?.find(
      (step) => step.order === order,
    );

    if (learningPath?.learning_path_type === 'evaluative') {
      return navigate({
        pathname: `/atividades/simulado/${stepByOrder?.activity_id}/resultado`,
        search: `trilha=${learningPath.id}&step=${stepByOrder?.id}&review=true`,
      });
    }

    redirectToStep(stepByOrder);
  }

  function goToStepByOrder(order: number) {
    const isAvailableStep = order <= (learningPath?.step_number ?? 0);

    if (
      learningPath?.status === 'finished' ||
      ((learningPath?.status === 'in_progress' ||
        learningPath?.status === 'expired') &&
        isAvailableStep)
    ) {
      const stepByOrder = learningPath?.steps?.find(
        (step) => step.order === order,
      );

      redirectToStep(stepByOrder);
    }
  }

  function getStepIdByActivity(activityId?: number) {
    if (!learningPath || !activityId) return;
    return learningPath?.steps?.find((step) => step.activity_id === activityId)
      ?.id;
  }

  function isLastStepInLearningPath(activityId?: number): boolean {
    if (!learningPath || !activityId) return false;

    const learningPathSteps = learningPath.steps ?? [];
    if (!learningPathSteps.length) return false;

    const currentStep = learningPathSteps.find(
      (step) => step.activity_id === activityId,
    );

    if (!currentStep) return false;

    return !learningPathSteps.some((step) => step.order > currentStep.order);
  }

  // A write step must always land on the composition feedback when finished, wherever it sits in
  // the trail — not only when it happens to be the last step. NewReviewed then decides between the
  // regular feedback and the zeramento view from the composition's own status.
  // `activityId` is the route's composition_id: in a trail the write step's activity_id IS the
  // composition id (same identity nextStep already relies on), so matching on it is correct.
  function isWriteStep(activityId?: number): boolean {
    if (!learningPath || !activityId) return false;

    return (
      learningPath.steps?.find((step) => step.activity_id === activityId)
        ?.activity_type === 'write'
    );
  }

  return {
    learningPath,
    getStepIdByActivity,
    isLastStepInLearningPath,
    isWriteStep,
    navigateToLearningPath: {
      currentStep: goToCurrentStep,
      nextStep: goToNextStep,
      lastReviewOrFirstStep: goToLastReviewOrFirstStep,
      previousStep: goToPreviousStep,
      stepByOrder: goToStepByOrder,
      finishedPage: finishLearningPath,
    },
  };
}

export default useLearningPathNavigation;
